import { BehaviorSubject, Subject } from 'rxjs';

/** One undoable edit: how to apply it again and how to take it back, each through v1 and the renderer bridge. */
export interface UndoStep {
  label: string;
  undo(): Promise<void>;
  redo(): Promise<void>;
  /** Steps with the same key recorded within MERGE_WINDOW_MS of each other are one step (typing, nudging, a slider). */
  mergeKey?: string;
  /** Rows this step can bring back while it is in the history (an asset type an undo re-creates a placement of). */
  holds?: readonly string[];
  /** The rows this step writes, as `kind:id`; a change to one from elsewhere takes the step out of the history. */
  rows?: readonly string[];
  /** The deletions (restoreDeletion ids) this step holds now; they are purged once it leaves the history. */
  deletions?: () => readonly string[];
}

/** Headers a write carries to say how the undo stack treats it: part of a gesture, or not an edit at all. */
export interface WriteScope {
  readonly headers: Readonly<Record<string, string>>;
}

/** One user gesture in progress. Writes that carry its headers become one undo step together. */
export interface Gesture extends WriteScope {
  readonly id: string;
  readonly label: string;
}

/** What the undo and redo controls show: the label of the step each would apply, or null when there is none. */
export interface UndoState {
  undoLabel: string | null;
  redoLabel: string | null;
}

// The kinds of row the open scene's saves are kept for while offline (offlineSaves.ts); a step writing any other needs the server.
// Materials are also named per asset (`materials:<asset id>`) and as a whole (`material`)
const OFFLINE_KINDS = new Set(['asset', 'keyframe', 'clip', 'uiElement', 'material', 'materials', 'vfx', 'scene', 'story']);

/** Whether a step writing these rows (`kind:id`) can be undone or redone offline. */
export const worksOffline = (rows: readonly string[] | undefined): boolean =>
  (rows ?? []).every(row => OFFLINE_KINDS.has(row.split(':', 1)[0]));

/** An undo or redo that failed and was dropped; `complete` says whether what it had done was all put back. */
export interface UndoFailure {
  label: string;
  action: 'undo' | 'redo';
  complete: boolean;
}

export const GESTURE_HEADER = 'X-Lureo-Gesture';

/** Whether a write scope is an open gesture rather than a fixed treatment such as untracked. */
export const isGesture = (scope: WriteScope): scope is Gesture => 'id' in scope;

/** An open gesture's steps so far, and how many of its writes are still in flight. */
type Group = { steps: UndoStep[]; inFlight: number; idle: Array<() => void> };

const MAX_STEPS = 100;

export const MERGE_WINDOW_MS = 1000;

let gestureCount = 0;

/** Errors of gestures that failed with rollBackOnError and whose every step was undone. */
const rolledBackErrors = new WeakSet<object>();

/** Whether the gesture that threw `error` with rollBackOnError was fully put back; false when any of its steps could not be undone. */
export const rolledBack = (error: unknown): boolean => !!error && typeof error === 'object' && rolledBackErrors.has(error);

/** Errors of gestures that failed after their scene closed, so their rollback was left to that scene. */
const abandonedErrors = new WeakSet<object>();

/** Whether the gesture that threw `error` failed after another scene opened: nothing of it was rolled back here. */
export const abandoned = (error: unknown): boolean => !!error && typeof error === 'object' && abandonedErrors.has(error);

const markAbandoned = (error: unknown) => {
  if (error && typeof error === 'object') abandonedErrors.add(error);
};

/** Errors of undos and redos that failed part-way and could not put back all they had done. */
const incompleteErrors = new WeakSet<object>();

/** `error`, marked as one after which part of the step may still be applied. */
export function incomplete(error: unknown): unknown {
  const marked = error && typeof error === 'object' ? error : new Error(String(error));
  incompleteErrors.add(marked);
  return marked;
}

/** Whether `error` came from an undo or redo that may have left part of its step applied. */
export const isIncomplete = (error: unknown): boolean => !!error && typeof error === 'object' && incompleteErrors.has(error);

/**
 * Applies the parts of one undo or redo in turn (a gesture's steps, a batch's rows, a material's nodes). One
 * that fails puts back the parts already applied, newest first, so the step is never left half applied; the
 * error is marked incomplete when that fails too.
 */
export async function applyInTurn<T>(parts: T[], apply: (part: T) => Promise<void>, back: (part: T) => Promise<void>): Promise<void> {
  const done: T[] = [];
  try {
    for (const part of parts) {
      await apply(part);
      done.push(part);
    }
  } catch (error) {
    let complete = !isIncomplete(error);
    for (const part of done.reverse()) {
      try {
        await back(part);
      } catch (putBackError) {
        complete = false;
        console.error('[UndoStack] could not put back part of a step:', putBackError);
      }
    }
    throw complete ? error : incomplete(error);
  }
}

/**
 * The editor's session-local undo/redo: a stack of recorded edits. Undo and redo run one at a time in
 * order, after every open gesture and every tracked write in flight has finished, so a quick double press
 * never interleaves two writes, never takes back half a gesture, and never skips an edit whose save has
 * not answered yet. A step that fails is dropped, never retried.
 */
export class UndoStack {
  private past: UndoStep[] = [];
  private future: UndoStep[] = [];
  private groups = new Map<string, Group>();
  /** Each gesture still running, by id, until it has recorded its step or rolled back. */
  private closing = new Map<string, Promise<unknown>>();
  /** Gestures, tracked writes and held saves of the open scene still running; undo and redo wait for them. */
  private open = new Set<Promise<unknown>>();
  private queue: Promise<unknown> = Promise.resolve();
  private lastRecordedAt = 0;
  /** Bumped by clear(): a gesture opened before it records nothing when it closes, and an undo or redo queued before does nothing. */
  private generation = 0;
  /** The undo or redo now applying a step, and the generation it was pressed in. Only one applies at a time. */
  private running: { generation: number; done: Promise<boolean> } | null = null;
  /** The step an undo or redo is applying, out of both stacks meanwhile: whether a new edit cut the redo branch, or a change from elsewhere took it out. */
  private moving: { step: UndoStep; cut: boolean; forgotten: boolean } | null = null;
  /** How many steps in the history hold each row, and who waits for it to be held by none. */
  private held = new Map<string, number>();
  private releaseWaiters = new Map<string, Array<() => void>>();
  /** What says a step can't be applied now (one that needs the server, offline), or null; asked as each queued undo or redo runs. */
  private blocked: ((step: UndoStep) => boolean) | null = null;

  /** The labels the undo and redo controls show. */
  readonly state$ = new BehaviorSubject<UndoState>({ undoLabel: null, redoLabel: null });

  /** Each undo or redo of the open scene that failed and was dropped, for the editor to tell the user. */
  readonly failures$ = new Subject<UndoFailure>();

  /** Deletions no step in the history holds any more, for the editor to purge (purgeDeletions). */
  readonly purges$ = new Subject<string[]>();

  /**
   * Records an edit that just happened. With the id of an open gesture it joins that gesture's step;
   * otherwise it is a step of its own, even while some other gesture is open. A new edit ends the redo branch.
   */
  record(step: UndoStep, gestureId?: string): void {
    const group = gestureId ? this.groups.get(gestureId) : undefined;
    if (group) {
      group.steps.push(step);
      return;
    }
    const now = Date.now();
    const last = this.past.at(-1);
    if (step.mergeKey && last?.mergeKey === step.mergeKey && this.future.length === 0 && now - this.lastRecordedAt < MERGE_WINDOW_MS) {
      const merged: UndoStep = {
        ...step,
        undo: last.undo,
        holds: [...(last.holds ?? []), ...(step.holds ?? [])],
        rows: [...(last.rows ?? []), ...(step.rows ?? [])],
        deletions: () => [...(last.deletions?.() ?? []), ...(step.deletions?.() ?? [])],
      };
      this.past[this.past.length - 1] = merged;
      this.keep(merged);
      // Its deletions live on in the merged step
      this.forget([last], false);
    } else {
      this.past.push(step);
      this.keep(step);
      if (this.past.length > MAX_STEPS) this.forget([this.past.shift()!]);
    }
    this.lastRecordedAt = now;
    this.forget(this.future);
    this.future = [];
    if (this.moving) this.moving.cut = true;
    this.publish();
  }

  /**
   * Runs one gesture: every write `edit` makes with the gesture's headers is recorded as one step, undone
   * in reverse order. Inside `within`, an open gesture, `edit` joins it instead of starting another.
   * A gesture whose `edit` throws still records the writes that succeeded, so a partial edit stays undoable;
   * what it writes to put things back must be untracked. With `rollBackOnError` (all or nothing) its steps
   * are undone instead, newest first, and nothing is recorded; `rolledBack(error)` says whether all of them were.
   * Joined to `within` with `rollBackOnError`, only the steps it added are undone and taken out of that gesture,
   * which goes on (a swap that fails inside a master update).
   */
  transaction<T>(
    label: string,
    edit: (gesture: Gesture) => Promise<T>,
    within?: Gesture,
    options: { rollBackOnError?: boolean } = {},
  ): Promise<T> {
    const joined = within ? this.groups.get(within.id) : undefined;
    if (within && joined) return options.rollBackOnError ? this.joinRollingBack(label, edit, within, joined) : edit(within);
    gestureCount += 1;
    const id = `g${gestureCount}`;
    const gesture: Gesture = { id, label, headers: { [GESTURE_HEADER]: id } };
    const group: Group = { steps: [], inFlight: 0, idle: [] };
    const generation = this.generation;
    this.groups.set(id, group);
    const run = (async () => {
      let failed: { error: unknown } | null = null;
      try {
        return await edit(gesture);
      } catch (error) {
        failed = { error };
        throw error;
      } finally {
        // Writes the gesture started but did not wait for still belong to it
        while (group.inFlight > 0) await new Promise<void>(resolve => group.idle.push(resolve));
        this.groups.delete(id);
        // A gesture of a scene that has since closed belongs to no history now open
        if (generation !== this.generation) {
          if (failed) markAbandoned(failed.error);
        } else {
          if (failed && options.rollBackOnError) {
            const complete = await this.rollBack(label, group.steps);
            if (complete && failed.error && typeof failed.error === 'object') rolledBackErrors.add(failed.error);
          } else this.recordGesture(label, group.steps);
        }
        // Recorded nowhere: what its steps deleted can never be restored
        if (generation !== this.generation || (failed && options.rollBackOnError)) this.purge(group.steps);
      }
    })();
    this.open.add(run);
    this.closing.set(id, run);
    const settle = () => {
      this.open.delete(run);
      this.closing.delete(id);
    };
    run.then(settle, settle);
    return run;
  }

  /** Resolves once these gestures have closed (recorded or rolled back) and no undo or redo is waiting or applying. */
  async settled(gestureIds: readonly string[]): Promise<void> {
    await Promise.allSettled(gestureIds.map(id => this.closing.get(id)));
    await this.queue;
  }

  /** Runs `edit` inside the open gesture `within`; if it throws, the steps it added are undone and dropped from it. */
  private async joinRollingBack<T>(label: string, edit: (gesture: Gesture) => Promise<T>, within: Gesture, group: Group): Promise<T> {
    const generation = this.generation;
    const from = group.steps.length;
    try {
      return await edit(within);
    } catch (error) {
      // Its writes still in flight are its steps too
      while (group.inFlight > 0) await new Promise<void>(resolve => group.idle.push(resolve));
      const own = group.steps.splice(from);
      if (generation !== this.generation) markAbandoned(error);
      else if ((await this.rollBack(label, own)) && error && typeof error === 'object') rolledBackErrors.add(error);
      this.purge(own);
      throw error;
    }
  }

  /**
   * Notes a tracked write in flight, of the open gesture `gestureId` if given; call the returned function once
   * it has been recorded or has failed. Undo and redo wait for it, and its gesture does not close before it.
   */
  writeStarted(gestureId?: string): () => void {
    const group = gestureId ? this.groups.get(gestureId) : undefined;
    if (group) group.inFlight += 1;
    let settle!: () => void;
    const write = new Promise<void>(resolve => { settle = resolve; });
    this.open.add(write);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.open.delete(write);
      settle();
      if (!group) return;
      group.inFlight -= 1;
      if (group.inFlight === 0) group.idle.splice(0).forEach(resolve => resolve());
    };
  }

  /**
   * Holds undo and redo (and the gesture `gestureId`, if given) until `save` settles: a save of several
   * requests in turn (page after page, a batch sent again without a row deleted meanwhile) is one wait, so
   * an undo never slips in between them. Resolves as `save` does.
   */
  hold<T>(save: Promise<T>, gestureId?: string): Promise<T> {
    const finished = this.writeStarted(gestureId);
    save.then(finished, finished);
    return save;
  }

  /**
   * Whether the undo or redo applying now (if any) was pressed in the scene still open. One that was not
   * finishes its writes for its own scene, but brings neither the renderer nor the editor along.
   */
  inOpenScene(): boolean {
    return !this.running || this.running.generation === this.generation;
  }

  /**
   * Resolves once no step in the history holds `key`: at once when none does, otherwise when the last such step
   * leaves it (the history is cleared, the step is pushed out by newer ones or by a new edit after undos, or it fails).
   */
  released(key: string): Promise<void> {
    if (!this.held.has(key)) return Promise.resolve();
    return new Promise(resolve => {
      const waiting = this.releaseWaiters.get(key) ?? [];
      waiting.push(resolve);
      this.releaseWaiters.set(key, waiting);
    });
  }

  /**
   * Takes out of the history every step that writes one of these rows (`kind:id`), since the row was changed
   * from elsewhere and the step would put back what is no longer there.
   */
  forgetRows(keys: readonly string[]): void {
    if (keys.length === 0) return;
    const touches = (step: UndoStep) => (step.rows ?? []).some(row => keys.includes(row));
    if (this.moving && touches(this.moving.step)) this.moving.forgotten = true;
    const dropped = [...this.past, ...this.future].filter(touches);
    if (dropped.length === 0) return;
    this.past = this.past.filter(step => !touches(step));
    this.future = this.future.filter(step => !touches(step));
    // The step before a dropped one is no merge target for the next edit
    this.lastRecordedAt = 0;
    this.forget(dropped);
    this.publish();
  }

  /** Every deletion the history still holds, for purging as the page goes away. */
  deletionIds(): string[] {
    return [...this.past, ...this.future].flatMap(step => step.deletions?.() ?? []);
  }

  /** Leaves a step where it is, unapplied, while `blocked` says so. */
  blockWhen(blocked: ((step: UndoStep) => boolean) | null): void {
    this.blocked = blocked;
  }

  /** Takes back the latest edit; resolves false when there was none, it is blocked, or it failed. */
  undo(): Promise<boolean> {
    return this.enqueue(() => this.move('undo'));
  }

  /** Applies the latest undone edit again; resolves false when there was none, it is blocked, or it failed. */
  redo(): Promise<boolean> {
    return this.enqueue(() => this.move('redo'));
  }

  /**
   * Forgets every step, e.g. when another scene opens, with the steps of gestures still open and any undo or
   * redo still queued. The old scene's writes in flight no longer hold the new scene's undo and redo; one
   * still applying finishes first, so two never interleave, and its step goes back into no history.
   */
  clear(): void {
    this.generation += 1;
    this.open = new Set();
    this.queue = this.running?.done ?? Promise.resolve();
    this.purge([...this.past, ...this.future]);
    this.past = [];
    this.future = [];
    const waiting = [...this.releaseWaiters.values()].flat();
    this.held.clear();
    this.releaseWaiters.clear();
    waiting.forEach(resolve => resolve());
    this.publish();
  }

  private keep(step: UndoStep): void {
    for (const key of step.holds ?? []) this.held.set(key, (this.held.get(key) ?? 0) + 1);
  }

  /** Hands what these steps deleted to purges$: no step holds it any more. */
  private purge(steps: UndoStep[]): void {
    const ids = steps.flatMap(step => step.deletions?.() ?? []);
    if (ids.length > 0) this.purges$.next(ids);
  }

  private forget(steps: UndoStep[], purge = true): void {
    if (purge) this.purge(steps);
    for (const key of steps.flatMap(step => step.holds ?? [])) {
      const count = (this.held.get(key) ?? 0) - 1;
      if (count > 0) {
        this.held.set(key, count);
        continue;
      }
      this.held.delete(key);
      const waiting = this.releaseWaiters.get(key) ?? [];
      this.releaseWaiters.delete(key);
      waiting.forEach(resolve => resolve());
    }
  }

  /** Undoes a failed gesture's steps, newest first; resolves whether every one of them was undone. */
  private async rollBack(label: string, steps: UndoStep[]): Promise<boolean> {
    let complete = true;
    for (const step of [...steps].reverse()) {
      try {
        await step.undo();
      } catch (error) {
        complete = false;
        console.error(`[UndoStack] rolling back '${label}' could not undo '${step.label}':`, error);
      }
    }
    return complete;
  }

  private recordGesture(label: string, steps: UndoStep[]): void {
    const holds = steps.flatMap(step => step.holds ?? []);
    const rows = steps.flatMap(step => step.rows ?? []);
    const deletions = () => steps.flatMap(step => step.deletions?.() ?? []);
    if (steps.length === 1) this.record({ label, undo: steps[0].undo, redo: steps[0].redo, holds, rows, deletions });
    else if (steps.length > 1) {
      this.record({
        label,
        holds,
        rows,
        deletions,
        undo: () => applyInTurn([...steps].reverse(), step => step.undo(), step => step.redo()),
        redo: () => applyInTurn(steps, step => step.redo(), step => step.undo()),
      });
    }
  }

  private enqueue(run: () => Promise<boolean>): Promise<boolean> {
    const generation = this.generation;
    // Pressed in a scene that has since closed: the history it meant is gone
    const afterWrites = () => Promise.allSettled([...this.open]).then(() => {
      if (generation !== this.generation) return false;
      // Marked before the step starts, so even its first update knows which scene it is for
      const running = { generation, done: Promise.resolve(false) };
      this.running = running;
      running.done = run().finally(() => {
        if (this.running === running) this.running = null;
      });
      return running.done;
    });
    const next = this.queue.then(afterWrites, afterWrites);
    this.queue = next;
    return next;
  }

  private async move(action: 'undo' | 'redo'): Promise<boolean> {
    const from = action === 'undo' ? this.past : this.future;
    // Asked here, not as it was pressed: presses queued behind a save can reach a different step by the time they run
    const next = from.at(-1);
    if (!next || this.blocked?.(next)) return false;
    // An edit after an undo or redo never merges into the step it moved
    this.lastRecordedAt = 0;
    const step = from.pop()!;
    const moving = { step, cut: false, forgotten: false };
    this.moving = moving;
    try {
      await (action === 'undo' ? step.undo() : step.redo());
      // Onto the stacks as they are now; one forgotten meanwhile, or undone while a new edit cut its redo branch, goes
      if (!this.inOpenScene()) this.purge([step]);
      else if (moving.forgotten || (action === 'undo' && moving.cut)) this.forget([step]);
      else (action === 'undo' ? this.future : this.past).push(step);
      return true;
    } catch (error) {
      console.error(`[UndoStack] '${step.label}' failed and was dropped:`, error);
      // A step of a scene since closed was forgotten with its history
      if (this.inOpenScene()) {
        this.forget([step]);
        this.failures$.next({ label: step.label, action, complete: !isIncomplete(error) });
      } else this.purge([step]);
      return false;
    } finally {
      if (this.moving === moving) this.moving = null;
      this.publish();
    }
  }

  private publish(): void {
    this.state$.next({
      undoLabel: this.past.at(-1)?.label ?? null,
      redoLabel: this.future.at(-1)?.label ?? null,
    });
  }

  /** The rows the step undo or redo would apply now writes (`kind:id`); state$ emits whenever that step changes. */
  nextRows(action: 'undo' | 'redo'): readonly string[] {
    return (action === 'undo' ? this.past : this.future).at(-1)?.rows ?? [];
  }
}
